import { useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, type Room, type User } from "./lib/api";
import { Auth } from "./components/Auth";
import { Rooms } from "./components/Rooms";
import { RoomView } from "./components/Room";
import { Transcripts } from "./components/Transcripts";
import { UnlockSettings } from "./components/UnlockSettings";
import { Admin } from "./components/Admin";

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [active, setActive] = useState<Room | null>(null);
  const [view, setView] = useState<"chat" | "transcripts" | "admin">("chat");
  const [canTranscripts, setCanTranscripts] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  useEffect(() => {
    if (!getToken()) {
      setReady(true);
      return;
    }
    api
      .me()
      .then((r) => {
        setUser(r.user);
        setCanTranscripts(r.canTranscripts);
        setIsAdmin(r.isAdmin);
      })
      .catch(() => setToken(null))
      .finally(() => setReady(true));
  }, []);

  const refreshRooms = useCallback(async () => {
    const r = await api.listRooms();
    setRooms(r.rooms);
    return r.rooms;
  }, []);

  // Apply an in-place room update (e.g. expiry change) to both the list and the
  // active room so it survives reselecting/navigating without a full refetch.
  const updateRoom = useCallback((updated: Room) => {
    setRooms((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
    setActive((a) => (a && a.id === updated.id ? updated : a));
  }, []);

  useEffect(() => {
    if (user) refreshRooms();
  }, [user, refreshRooms]);

  function logout() {
    setToken(null);
    setUser(null);
    setRooms([]);
    setActive(null);
  }

  if (!ready) return <div className="center muted">loading…</div>;
  if (!user)
    return (
      <Auth
        onAuthed={(u) => {
          setUser(u);
          api
            .me()
            .then((r) => {
              setCanTranscripts(r.canTranscripts);
              setIsAdmin(r.isAdmin);
            })
            .catch(() => {});
        }}
      />
    );

  return (
    <div className="shell">
      <nav className="topnav">
        <button className={view === "chat" ? "primary" : "ghost"} onClick={() => setView("chat")}>
          Chat
        </button>
        {canTranscripts && (
          <button
            className={view === "transcripts" ? "primary" : "ghost"}
            onClick={() => setView("transcripts")}
          >
            Transcripts
          </button>
        )}
        {isAdmin && (
          <button
            className={view === "admin" ? "primary" : "ghost"}
            onClick={() => setView("admin")}
          >
            Admin
          </button>
        )}
        <div className="spacer" />
        <button className="ghost" onClick={() => setShowKeys(true)} title="Tap-to-unlock keys">
          🔑 Keys
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          {user.username}
        </span>
        <button className="ghost" onClick={logout}>
          Sign out
        </button>
      </nav>
      {showKeys && <UnlockSettings username={user.username} onClose={() => setShowKeys(false)} />}
      <div className="shell-body">
        {view === "admin" && isAdmin ? (
          <Admin />
        ) : view === "transcripts" && canTranscripts ? (
          <Transcripts />
        ) : (
          <div className="app">
            <Rooms
              user={user}
              rooms={rooms}
              active={active}
              onSelect={setActive}
              onChanged={refreshRooms}
              onLogout={logout}
            />
            <div className="main">
              {active ? (
                <RoomView key={active.id} room={active} user={user} onRoomUpdate={updateRoom} />
              ) : (
                <div className="center muted">
                  Select or create a room. Share the room id and the passphrase with your peer
                  out of band.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
